import React, {useState, useContext, useEffect} from "react";
import Dashview from "../Dashview";
import axios from 'axios';
import { Redirect } from "react-router-dom";
import { AppContext } from "AppContext";
import Tooltip from 'rc-tooltip';

const OrderSummary = (props) => {

    const { identity, config } = useContext(AppContext);
    const [ total_cost_visible, set_total_cost_visible ] = useState(false);
    const [ ship_addr_visible, set_ship_addr_visible ] = useState(false);
    const [ invoice_visible, set_invoice_visible ] = useState(false);

    const finishes = [
        { text: "MJF Finish",               value: "MJF_FINISH"},
        { text: "Vapor Smooth",             value: "VAPOR_SMOOTHING"},
        { text: "Black Dye",                value: "BLACK_DYE"},
        { text: "Vapor Smooth / Black Dye", value: "AMT_BLACK_DYE"},
        { text: "Sinter",                   value: "SINTER"},
        { text: "Depowder Only",            value: "DEPOWDER" }
    ];

    const units = [
        { text: "mm", value: "UNITS_MM"},
        { text: "in", value: "UNITS_IN"}
    ];

    function imgNotFound(image) {
        image.target.onerror = null;
        image.target.src = "/no_image.png";
        image.target.alt = "No image found";
        return true;
    }

    function prevent_default(e) {
        e.preventDefault();
    }

    function on_total_cost_visible_change(args) {
        set_total_cost_visible(!total_cost_visible);
    }

    function on_ship_addr_visible_change(args) {
        set_ship_addr_visible(!ship_addr_visible);
    }

    function on_invoice_visible_change(args) {
        set_invoice_visible(!invoice_visible);
    }

    // Calculate cost totals and format Total Cost Block
    const amount_surcharge = props.value.Surcharges.reduce((acc, curr) => acc + (curr.unit_price * curr.OrderSurcharge.surcharge_qty), 0.);
    const amount_shipping = props.value.amount_shipping;
    const total_cost = props.value.metadata.parts.reduce((acc, curr) => acc + curr.price_total, 0) +
        (amount_surcharge ?? 0) + (amount_shipping ?? 0) + (props.value.metadata.amount_tax ?? 0);

    const show_total_cost_block = props.value.Surcharges.length > 0 || amount_shipping > 0 || props.value.metadata.amount_tax;
    const subtotal_line = "Subtotal: " + props.value.metadata.parts.reduce((acc, curr) => acc + curr.price_total, 0).toFixed(2);
    const surcharge_line = amount_surcharge > 0 ? "\nSurcharge: " + amount_surcharge.toFixed(2) : "";
    const ship_cost_line = amount_shipping > 0 ? "\nShipping: " + amount_shipping.toFixed(2) : "";
    const tax_cost_line = props.value.metadata.amount_tax ? "\nTaxes: " + props.value.metadata.amount_tax.toFixed(2) : "";
    const total_cost_array = [subtotal_line, surcharge_line, ship_cost_line, tax_cost_line];

    // Pad each string so the decimal places will vertically align
    const rightmost_period = total_cost_array.reduce((furthest, current) => Math.max(current.replace("\n", "").indexOf("."), furthest), 0);
    const total_cost_block = total_cost_array.map((line => {
        const pad_amount = rightmost_period - line.replace("\n", "").indexOf(".");
        const padding = ":".padEnd(pad_amount + 1, " ");
        return line.replace(":", padding);
    })).join("");

    // Format Address Block
    const ship_address = props.value.Address;
    const city_state_line = ship_address ? ship_address.city + ", " + ship_address.state + " " + ship_address.zip : "";
    const address_block = ship_address ? [
        props.value.shipping_method,
        ship_address.name, 
        ship_address.org, 
        ship_address.address_1, 
        ship_address.address_2, 
        city_state_line,
        ship_address.country
    ].filter(Boolean).join("\n") : "";

    // Format Invoice Block
    const invoice_block = "Uploaded Invoice:\n" + props.value.original_invoice;

    return (
        <div className={"rounded-md border width-full mt-4"}>
            <div className={"width-full flex flex-row bg-gray-200 rounded-t p-4 space-x-8"}>
                <div className={"flex flex-col"}>
                    <div className={"text-sm font-semibold"}>Order Placed:</div>
                    <div>{props.value.created_at.toDateString()}</div>
                </div>
                <div className={"flex flex-col"}>
                    <div className={"text-sm font-semibold"}>Total Cost:</div>
                    { show_total_cost_block ? 
                    <Tooltip 
                        placement="bottom"
                        visible={total_cost_visible}
                        onVisibleChange={on_total_cost_visible_change}
                        trigger={["click"]}
                        overlay={
                            <p onClick={prevent_default}>{total_cost_block}</p>
                        }
                        overlayInnerStyle={{
                            background: 'white', 
                            color: 'black', 
                            fontFamily: 'monospace, monospace', 
                            fontSize: '1rem', 
                            lineHeight: '1.5rem', 
                            whiteSpace: 'pre'
                        }}
                    >
                        <button onClick={prevent_default} className={"w-fit cursor-pointer underline"}>
                            { total_cost.toFixed(2) }
                        </button>
                    </Tooltip>
                    :
                    <div>{ total_cost.toFixed(2) }</div>
                    }
                </div>
                { props.value.Address &&
                <div className={"flex flex-col"}>
                    <div className={"text-sm font-semibold"}>Shipped To:</div>
                    <Tooltip 
                        placement="bottom"
                        visible={ship_addr_visible}
                        onVisibleChange={on_ship_addr_visible_change}
                        trigger={["click"]}
                        overlay={
                            <p onClick={prevent_default}>{address_block}</p>
                        }
                        overlayInnerStyle={{
                            background: 'white', 
                            color: 'black', 
                            fontFamily: 'monospace, monospace', 
                            fontSize: '1rem', 
                            lineHeight: '1.5rem', 
                            whiteSpace: 'pre-line'
                        }}
                    >
                        <button onClick={prevent_default} className={"w-fit cursor-pointer underline"}>
                            { props.value.Address.name || props.value.Address.org || props.value.Address.address_1 }
                        </button>
                    </Tooltip>
                </div>
                }
                { props.value.purchase_order &&
                <div className={"flex flex-col"}>
                    <div className={"text-sm font-semibold"}>Purchase Order #:</div>
                    { props.value.original_invoice ?
                    <Tooltip
                        placement="bottom"
                        visible={invoice_visible}
                        onVisibleChange={on_invoice_visible_change}
                        trigger={["click"]}
                        overlay={
                            <p onClick={prevent_default}>{invoice_block}</p>
                        }
                        overlayInnerStyle={{
                            background: 'white', 
                            color: 'black', 
                            fontFamily: 'monospace, monospace', 
                            fontSize: '1rem', 
                            lineHeight: '1.5rem', 
                            whiteSpace: 'pre'
                        }}
                    >
                        <button onClick={prevent_default} className={"w-fit cursor-pointer underline 2xl:max-w-xs 2xl:truncate"}>
                            { props.value.purchase_order }
                        </button>
                    </Tooltip> 
                    :
                    <div className="2xl:max-w-xs 2xl:truncate">
                        { props.value.purchase_order }
                    </div>
                    }
                </div>
                }
                { props.value.stripe_status && props.value.stripe_status !== "Paid" &&
                <div className={"flex flex-col"}>
                    <div className={"text-sm font-semibold"}>Payment Status:</div>
                    <div className="2xl:max-w-xs 2xl:truncate">
                        { props.value.stripe_status }
                    </div>
                </div>
                }
                { props.value.stripe_status === 'Paid' && props.value.stripe_receipt &&
                <div className={"flex flex-col"}>
                    <div className={"text-sm font-semibold"}>CC Receipt:</div>
                    <div>
                        <a href={props.value.stripe_receipt} target="_blank" rel="noopener noreferrer">
                            <span class="underline">Open</span>&nbsp;
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 inline align-baseline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                            </svg>
                        </a>
                    </div>
                </div>
                }
                <div className={"flex flex-col flex-grow items-end"}>
                    <div className="text-right"><span className={"text-sm font-semibold"}>Order Id:</span> {props.value.id}</div>
                    { props.value.itar &&
                        <div className={"text-sm font-semibold"}>*ITAR Compliant</div>
                    }
                </div>
            </div>
            <div className={"width-full bg-white p-4"}>
                { props.value.metadata.parts.map((item, index) => {
                    return (
                        <div className={index === props.value.metadata.parts.length-1 ? "width-full" : "width-full mb-4 border-b pb-2 border-dashed border-gray-500"} key={index}>
                            <div className={"flex flex-cols space-x-4"}>
                                <div style={{ minWidth: "96px", width: "96px", height: "96px", minHeight: "96px"}}>
                                    <div ><img src={config.storage.host + "/orders/" + identity.userAcctId + "/" + item.snapshot_filename}
			                       onError={ (img) => imgNotFound(img) } alt={"Screenshot of part " + item.original_filename} /></div>
                                </div>
                                <div className={"grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-3 flex-grow"}> 
                                    <div className={"col-span-2 lg:col-span-3 xl:col-span-2"}>
                                        <div className={"text-sm font-semibold"}>
                                            Model filename:
                                        </div>
                                        <div className={"break-words"}>
                                            {item.original_filename.replaceAll("_", "_\u200B").replaceAll("-", "-\u200B")}
                                        </div>
                                    </div>
                                    <div>
                                        <div className={"text-sm font-semibold"}>
                                            Material:
                                        </div>
                                        <div>
                                            {item.material}
                                        </div>
                                    </div>
                                    <div>
                                        <div className={"text-sm font-semibold"}>
                                            Finish:
                                        </div>
                                        <div>
                                            { finishes.find((finishType) => finishType.value === item.finish).text }
                                        </div>
                                    </div>
                                    <div>
                                        <div className={"text-sm font-semibold"}>
                                            Volume:
                                        </div>
                                        <div>
                                            {item.volume.toFixed(2)} { units.find((unit) => unit.value === item.units).text }<sup>3</sup>
                                        </div>
                                    </div>
                                    <div>
                                        <div className={"text-sm font-semibold"}>
                                            Price:
                                        </div>
                                        <div>
                                            {item.price_per_component.toFixed(2)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className={"text-sm font-semibold"}>
                                            Quantity:
                                        </div>
                                        <div>
                                            {item.quantity}
                                        </div>
                                    </div>
                                    <div>
                                        <div className={"text-sm font-semibold"}>
                                            Total Cost:
                                        </div>
                                        <div>
                                            {item.price_total.toFixed(2)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {item.comments !== "" &&
                            <div className={"pr-2 pl-2 pt-2"}>
                                <label className="block">
                                    <div className="text-sm font-semibold">
                                        Special Instructions / Comments:
                                    </div>
                                    <div>
                                        {item.comments.trim().replaceAll("\n", " || ")}
                                    </div>
                                </label>
                            </div>
                            }
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

/** QuoteItem was not being used, commented out - 10/21/24 ez
const QuoteItem = (props) => {

    // Retrieve configuration information from global context:
    const { identity, config } = useContext(AppContext);
    const [redirect, setRedirect] = useState(false);

    var link = "/dashboard/orders/" + props.value.id;

    let timestamp = props.value.created_at;
    let formatted = '';
    formatted += (timestamp.getMonth() +1 );
    formatted += '/' + (timestamp.getDay().length < 2 ? '0' + timestamp.getDay() : timestamp.getDay());
    formatted += '/' + timestamp.getFullYear();
    formatted += ' - ';
    formatted += (timestamp.getHours().length < 2 ? '0' + timestamp.getHours() : timestamp.getHours());
    formatted += ':';
    formatted += (timestamp.getMinutes().length < 2 ? '0' + timestamp.getMinutes() : timestamp.getMinutes());

    const bgcolor = props.index % 2 === 0 ? "bg-green-50"   : "bg-green-100";
    const hvcolor = props.index % 2 === 0 ? "bg-green-200"  : "bg-green-300";

    // Determine the material name:
    function getMaterialText(m) {
        console.log("looking up: " + m)
        console.log(props.materials)
        let materialText = "unknown";
        for (let x = 0; x != props.materials.length; x++) {
            if (props.materials[x].id === m) {
                materialText = props.materials[x].name;
            }
        }
        return materialText;
    }

    if(redirect === true) {
        return  <Redirect to={{ pathname: link }} />
    }

    return(
        <div className={"flex mt-4 space-x-4 border-b-2 " + bgcolor + " hover:" + hvcolor } onClick={(e) => setRedirect(true)}>
            <div>
                <img src={'/icon-file-stl.png'} width={'64px'} height={'64px'}></img>
            </div>
            <div className={'flex flex-col'}>
                { props.value.metadata.parts.map((item, index) => {
                    return <div key={index}><img src={config.storage.host + "/orders/" + identity.userAcctId + "/" + item.snapshot_filename} width={96} height={96}/></div>
                })}
            </div>
            <div className={'flex flex-col'}>
                { props.value.metadata.parts.map((item, index) => {
                    return (
                        <div key={index}>
                            <span>Material: { getMaterialText(item.material) }</span>
                            <span className={"ml-4"}>Quantity: { item.quantity }</span>
                            <span className={"ml-4"}>Price: ${ item.price}</span>
                            <span className={"ml-4"}>Total Cost: ${ item.quantity * item.price }</span>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
 */

const Content = (props) => {

    // const history = useHistory();
    const { identity, config } = useContext(AppContext);
    const [redirect, set_redirect] = useState({ redirect: false, path: ""})
    const [orders, set_orders] = useState([]);
    const [materials, set_materials] = useState([]);
    const [account, set_account] = useState(null);
    const [loading, set_loading] = useState(true);

    useEffect(()=>{
        // Retrieve all quotes from the server:
        const action = async () => {
            try {
                // Retrieve material data from the server:
                const rxMaterials = await axios.get(config.api.base + '/materials');
                set_materials(rxMaterials.data);
                const account_api_response = await axios.get(config.api.base + '/admin/accounts/' + identity.userAcctId);
                set_account(account_api_response.data[0]);
                const response = await axios.get(config.api.base + '/orders/');
                set_loading(false);
                set_orders(response.data);
                console.log("::::: ORDER RESPONSE :::::")
                console.log(response.data);
            } catch(x) {
                console.log(x);
            }

        }
        action();

    }, [config.api.base, identity.userAcctId])


    function on_click_create_order() {
        set_redirect({ redirect: true, path: "/dashboard/orders/new"})
    }

    if(redirect.redirect === true) {
        return <Redirect push to={redirect.path}/>
    }

    if (account === null) return null;
    const orders_disabled = account.card_payment === null || account.tax_exempt === null || account.fob === null;

    if (loading) {
        return (
            <div className={"container mx-auto"}>
                <div className={"flex w-full border-b-2 border-solid py-4"}>
                    <div>
                        <span className={"text-3xl"}>
                            Orders{ identity.userAdmin && ` - ${identity.userComp}` }
                        </span>
                    </div>
                </div>
                <div className={"flex flex-col flex-grow w-full h-full justify-center items-center"}>
                    <div className={"mt-48"}>
                        <span className={"text-2xl text-gray-700"}>
                            Loading...
                        </span>
                    </div>
                    <div style={{ width: "240px", height: "240px"}}>
                        <img src={"/loading-transparent.gif"} style={{ height: "100%"}} alt="A loading gif - Many 2D planes moving and intersecting in 3D" />
                    </div>
                </div>
            </div>
        )
    } else {
        return (
            <div className={"container mx-auto"}>
                <div className={"flex w-full border-b-2 border-solid py-4"}>
                    <div>
                        <span className={"text-3xl"}>
                            Orders{ identity.userAdmin && ` - ${identity.userComp}` }
                        </span>
                    </div>
                    { identity.userAdmin &&
                        <div className="flex flex-grow justify-end">
                            <a className="standardButton" href={"/admin/dashboard/orders"}>Go to All Orders</a>
                        </div>
                    }
                    <div className={identity.userAdmin ? "ml-4" : "flex-grow ml-4"}>
                        <div className={"flex h-full justify-end items-center "}>
                            <button className={"green-button h-10"} disabled={orders_disabled} onClick={on_click_create_order} style={{ marginRight: '5px'}}>
                                New Order
                            </button>
                        </div>
                    </div>
                </div>
                { orders_disabled &&
                    <div className="errorBox my-4">
                        Your account setup has not been finalized. Please contact&nbsp;
                        <a className="underline" href="mailto:jobtravelers@abcorp.com">jobtravelers@abcorp.com</a>&nbsp;for inquires.
                    </div>
                }
                <div>
                    { orders.map((item, index) => {
                        return <OrderSummary value={item} materials={materials} key={index} index={index}/>
                    })}
                </div>
                <div>
                    { !orders_disabled &&
                        <div className={"my-4 infoBox"}>
                            <p>{ orders.length === 0 ? "No recent orders were found. " : "" }If you would like to find information concerning older orders, please contact us at&nbsp; 
                            <a className="underline text-green-900" href={"mailto:jobtravelers@abcorp.com?subject=Order%20History%20Request"}>
                                jobtravelers@abcorp.com
                            </a>.</p>
                        </div>
                    }
                </div>
                { (orders.length > 5 || orders.reduce((num_parts, order) => num_parts + order.metadata.parts.length, 0) > 8) &&
                    <div className={"flex pt-2 pb-4 justify-center"}>
                        <button onClick={ () =>  window.scroll({top: 0, behavior: 'smooth'}) } className={"cursor-pointer text-sm text-indigo-500"}>
                            Jump to Top
                        </button>
                    </div>
                }
            </div>
        )
    }
}


const Orders = (props) => {
    return (<Dashview {...props}><Content/></Dashview>);
}

export default Orders;
